// src/components/TaxSummary/TaxSummary.js
import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../api';
import { AVAILABLE_YEARS } from '../../utils/incomeConstants';
import { calculateFullIR } from '../../utils/incomeCalculations';
import './TaxSummary.css';

const TaxSummary = ({ individual, individualId, individualName }) => {
  const [incomes, setIncomes] = useState([]);
  const [selectedYear, setSelectedYear] = useState(AVAILABLE_YEARS[0]);
  const [isLoading, setIsLoading] = useState(true);
  const [irResult, setIrResult] = useState(null);

  const loadIncomes = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await apiService.fetchIncomes(individualId);
      setIncomes(data);
    } catch (error) {
      console.error('Error loading incomes:', error);
    } finally {
      setIsLoading(false);
    }
  }, [individualId]);

  useEffect(() => {
    loadIncomes();
  }, [loadIncomes]);

  useEffect(() => {
    if (individual && incomes.length >= 0) {
      const result = calculateFullIR(individual, incomes, selectedYear);
      setIrResult(result);
    }
  }, [individual, incomes, selectedYear]);

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  if (isLoading) {
    return <div className="tax-loading">Chargement...</div>;
  }

  if (!irResult) {
    return <div className="tax-loading">Calcul en cours...</div>;
  }

  return (
    <div className="tax-summary-container">
      <div className="tax-header">
        <h4>Simulation IR - {individualName}</h4>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
          className="year-selector"
        >
          {AVAILABLE_YEARS.map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
      </div>

      {irResult.incomeCount === 0 ? (
        <div className="no-incomes-warning">
          Aucun revenu enregistré pour {selectedYear}.
          <br />
          Ajoutez des revenus dans l'onglet "Revenus" pour voir la simulation fiscale.
        </div>
      ) : (
        <>
          {/* Section Revenus */}
          <div className="tax-section">
            <h5>Revenus {selectedYear}</h5>
            <table className="tax-table">
              <tbody>
                <tr>
                  <td>Revenus bruts déclarés</td>
                  <td className="amount">{formatCurrency(irResult.totalGrossIncome)}</td>
                </tr>
                <tr className="highlight">
                  <td><strong>Revenu net imposable</strong></td>
                  <td className="amount"><strong>{formatCurrency(irResult.totalTaxableIncome)}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Section Quotient Familial */}
          <div className="tax-section">
            <h5>Quotient familial</h5>
            <table className="tax-table">
              <tbody>
                <tr>
                  <td>Situation</td>
                  <td className="value">
                    {irResult.isCouple ? 'Couple' : 'Célibataire'}
                    {irResult.childrenCount > 0 && ` + ${irResult.childrenCount} enfant(s)`}
                    {irResult.singleParent && ' (parent isolé)'}
                  </td>
                </tr>
                <tr>
                  <td>Nombre de parts</td>
                  <td className="value"><strong>{irResult.parts}</strong></td>
                </tr>
                <tr>
                  <td>Quotient familial (R/N)</td>
                  <td className="amount">{formatCurrency(irResult.quotient)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Section Calcul IR */}
          <div className="tax-section">
            <h5>Calcul de l'impôt</h5>
            <table className="tax-table">
              <tbody>
                <tr>
                  <td>IR de référence (sans QF)</td>
                  <td className="amount">{formatCurrency(irResult.grossTaxReference)}</td>
                </tr>
                <tr>
                  <td>IR avec quotient familial</td>
                  <td className="amount">{formatCurrency(irResult.grossTaxWithQuotient)}</td>
                </tr>
                <tr className={irResult.isCapped ? 'warning' : ''}>
                  <td>
                    Réduction quotient familial
                    {irResult.isCapped && <span className="capped-badge">PLAFONNÉ</span>}
                  </td>
                  <td className="amount negative">
                    -{formatCurrency(irResult.cappedReduction)}
                    {irResult.isCapped && (
                      <span className="capped-info"> (max: {formatCurrency(irResult.maxQuotientReduction)})</span>
                    )}
                  </td>
                </tr>
                {irResult.decoteApplied && (
                  <tr>
                    <td>Décote</td>
                    <td className="amount negative">-{formatCurrency(irResult.decoteAmount)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Section Résultat */}
          <div className="tax-section result-section">
            <h5>Résultat</h5>
            <table className="tax-table">
              <tbody>
                <tr className="total-row">
                  <td><strong>Impôt sur le revenu {selectedYear}</strong></td>
                  <td className="amount tax-amount">
                    <strong>{formatCurrency(irResult.finalTax)}</strong>
                  </td>
                </tr>
                <tr>
                  <td>Taux marginal d'imposition (TMI)</td>
                  <td className="value tmi">{irResult.marginalRatePercent}%</td>
                </tr>
                <tr>
                  <td>Taux moyen d'imposition</td>
                  <td className="value">{irResult.averageRatePercent}%</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Barème */}
          <div className="tax-section bareme-info">
            <h5>Barème IR {selectedYear}</h5>
            <div className="bareme-grid">
              <div className="bareme-item">0% jusqu'à 11 294€</div>
              <div className="bareme-item">11% de 11 294€ à 28 797€</div>
              <div className="bareme-item">30% de 28 797€ à 82 341€</div>
              <div className="bareme-item">41% de 82 341€ à 177 106€</div>
              <div className="bareme-item">45% au-delà de 177 106€</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TaxSummary;
